import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import EmailIcon from "@mui/icons-material/Email";
import StarIcon from "@mui/icons-material/Star";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useNavigate } from "react-router-dom";

import PageTitle from "../../components/Page Component/PageTitle";
import { RootState } from "../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  fetchData,
  postData,
} from "../../store/actions";
import { useCustomToast } from "../../components/Methods/SnackBar";
import {
  FEEDBACK_REQUESTS_API,
  FEEDBACK_REQUESTS_EXPORT_API,
} from "../../Service/Apis";
import CreateFeedbackRequest from "./CreateFeedbackRequest";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Represents a single client feedback request as returned by the list API.
 * `is_submitted` is used separately from `status` because it drives which
 * action buttons are available (view vs. resend).
 */
interface FeedbackRequest {
  request_id: string;
  /** One-time token embedded in the public feedback URL sent to the client. */
  token: string;
  employee_name: string;
  project_name: string | null;
  client_name: string;
  client_email: string;
  template_name: string;
  created_at: string;
  expires_at: string;
  status: "Pending" | "Submitted" | "Expired";
  /** Convenience flag — true when a response has been recorded for this request. */
  is_submitted: boolean;
  /** Overall rating given by the client (null when not yet submitted). */
  overall_rating: number | null;
  /** Maximum possible rating for the template (e.g. 5 or 10). */
  rating_scale: number;
}

/**
 * Maps a feedback request status to the corresponding MUI Chip colour.
 * Returns "default" for any unknown status to avoid rendering errors.
 */
const statusColor = (
  status: string
): "warning" | "success" | "error" | "default" => {
  if (status === "Submitted") return "success";
  if (status === "Expired") return "error";
  if (status === "Pending") return "warning";
  return "default";
};

/**
 * Formats an ISO date string into a short human-readable date (e.g. "23 Apr 2026")
 * using the Indian locale. Returns an em-dash when the value is empty or falsy.
 */
const formatDate = (iso: string): string => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Full-page view that lists all client feedback requests visible to the current user.
 *
 * Features:
 * - Free-text search across employee name, client name, email, and project.
 * - Filter by status (Pending / Submitted / Expired) and by template.
 * - Role-gated "New Request" button — only Admin, HR, and Manager can create requests.
 * - Per-row actions: view submitted response, resend invitation email, copy feedback link.
 */
const FeedbackRequestList: React.FC = () => {
  const [requests, setRequests] = useState<FeedbackRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [templateFilter, setTemplateFilter] = useState<string>("all");
  const [search, setSearch] = useState<string>("");
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);

  const showToast = useCustomToast();
  const navigate = useNavigate();
  const selectedRole = useSelector(
    (state: RootState) => state.auth.selectedRole
  );

  const dispatch: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  /** Fetches the full list of feedback requests from the API and updates local state. */
  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await dispatch(fetchData(FEEDBACK_REQUESTS_API));
      setRequests(Array.isArray(data) ? data : data?.results ?? []);
    } catch {
      showToast("Failed to load feedback requests.", "error", false);
    } finally {
      setLoading(false);
    }
  }, [dispatch, showToast]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  /**
   * Triggers a reminder email for the given pending feedback request.
   * Disabled for requests that are already submitted or expired,
   * and uses per-row loading state to prevent duplicate dispatches.
   */
  const handleResend = async (requestId: string) => {
    setResendingId(requestId);
    try {
      await dispatch(
        postData(`${FEEDBACK_REQUESTS_API}${requestId}/resend/`, {})
      );
      showToast("Reminder email sent successfully.", "success", false);
    } catch {
      showToast("Failed to resend email.", "error", false);
    } finally {
      setResendingId(null);
    }
  };

  /**
   * Constructs the public feedback URL from the request token and writes it to
   * the clipboard. Useful for manually sharing the link when the client did not
   * receive or has lost the original email.
   */
  const handleCopyLink = async (token: string) => {
    const link = `${window.location.origin}/feedback/${token}`;
    try {
      await navigator.clipboard.writeText(link);
      showToast("Feedback link copied to clipboard!", "success", false);
    } catch {
      showToast("Failed to copy link.", "error", false);
    }
  };

  // ---------------------------------------------------------------------------
  // Filtered rows
  // ---------------------------------------------------------------------------
  // Unique template names for the filter dropdown
  const templateOptions = Array.from(
    new Set(requests.map((r) => r.template_name).filter(Boolean))
  ).sort();

  const filtered = requests.filter((r) => {
    const matchStatus = statusFilter === "all" || r.status === statusFilter;
    const matchTemplate = templateFilter === "all" || r.template_name === templateFilter;
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      r.employee_name?.toLowerCase().includes(q) ||
      r.client_name?.toLowerCase().includes(q) ||
      r.client_email?.toLowerCase().includes(q) ||
      r.project_name?.toLowerCase().includes(q);
    return matchStatus && matchTemplate && matchSearch;
  });

  /**
   * Exports the currently filtered set of feedback requests to an Excel
   * workbook (one sheet per request, columns Employee..Rating followed by
   * per-parameter rating columns). Falls back to exporting everything the
   * user can see when no filters are applied.
   */
  const handleExport = async () => {
    setExporting(true);
    try {
      const ids = filtered.map((r) => r.request_id).join(",");
      const url = ids
        ? `${FEEDBACK_REQUESTS_EXPORT_API}?ids=${encodeURIComponent(ids)}`
        : FEEDBACK_REQUESTS_EXPORT_API;
      const token = localStorage.getItem("token");
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      a.href = downloadUrl;
      a.download = `client_feedback_${today}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
    } catch {
      showToast("Failed to export feedback requests.", "error", false);
    } finally {
      setExporting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
          mb: 1,
        }}
      >
        <PageTitle>Client Feedback Requests</PageTitle>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 1, ml: "auto" }}>
          {(selectedRole?.toLowerCase() === "admin" ||
            selectedRole?.toLowerCase() === "hr" ||
            selectedRole?.toLowerCase() === "manager") && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
            >
              New Request
            </Button>
          )}
          <Button
            variant="contained"
            color="success"
            startIcon={
              exporting ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : (
                <DownloadIcon />
              )
            }
            onClick={handleExport}
            disabled={exporting || loading || filtered.length === 0}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField
          label="Search"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Employee, client, project…"
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="Pending">Pending</MenuItem>
            <MenuItem value="Submitted">Submitted</MenuItem>
            <MenuItem value="Expired">Expired</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Template</InputLabel>
          <Select
            label="Template"
            value={templateFilter}
            onChange={(e) => setTemplateFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            {templateOptions.map((tmpl) => (
              <MenuItem key={tmpl} value={tmpl}>
                {tmpl}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "primary.main" }}>
                {[
                  "Employee",
                  "Project",
                  "Client Name",
                  "Client Email",
                  "Sent On",
                  "Expires At",
                  "Status",
                  "Rating",
                  "Actions",
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{ color: "#fff", fontWeight: 600 }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No feedback requests found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((req) => (
                  <TableRow key={req.request_id} hover>
                    <TableCell>{req.employee_name}</TableCell>
                    <TableCell>{req.project_name ?? "—"}</TableCell>
                    <TableCell>{req.client_name}</TableCell>
                    <TableCell>{req.client_email}</TableCell>
                    <TableCell>{formatDate(req.created_at)}</TableCell>
                    <TableCell>{formatDate(req.expires_at)}</TableCell>
                    <TableCell>
                      <Chip
                        label={req.status}
                        color={statusColor(req.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {req.overall_rating != null ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <StarIcon sx={{ color: "#f59e0b", fontSize: 20 }} />
                          <Typography variant="body2" fontWeight={600} fontSize={15}>
                            {req.overall_rating}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" fontSize={15}>
                            /{req.rating_scale}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        {req.is_submitted && (
                          <Tooltip title="View Feedback">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() =>
                                navigate(
                                  `/feedback/requests/${req.request_id}`
                                )
                              }
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!req.is_submitted && req.status !== "Expired" && (
                          <Tooltip title="Resend Email">
                            <span>
                              <IconButton
                                size="small"
                                color="secondary"
                                disabled={resendingId === req.request_id}
                                onClick={() => handleResend(req.request_id)}
                              >
                                {resendingId === req.request_id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <EmailIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip title="Copy Feedback Link">
                          <IconButton
                            size="small"
                            onClick={() => handleCopyLink(req.token)}
                            sx={{ color: "text.secondary" }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create dialog */}
      <CreateFeedbackRequest
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          fetchRequests();
        }}
      />
    </Container>
  );
};

export default FeedbackRequestList;
